import { useEffect, useState } from 'react';
import { EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { useContextoReservas } from './contexto';
import { hoyEn } from './fechas';
import { CifrasSocio } from './dia/CifrasSocio';
import { ReservasDelDia } from './dia/ReservasDelDia';
import { FichaReserva } from './ficha/FichaReserva';
import { NuevaReserva } from './nueva/NuevaReserva';
import { PorCompletar } from './pendientes/PorCompletar';
import { PERMISOS } from './textos';
import '../../componentes/apartado.css';
import './reservas.css';

/**
 * «Reservas» (Operativa, entrada `reservas`, 8.1 y 8.4): la barra de apartados Reservas del día · Por completar en
 * `?apartado=` (`dia`, `por-completar`; Estancias, 8.9, tiene su propia entrada del menú, G878). «Por completar» solo llega a quien completa reservas o deshace no-shows; sin
 * `?apartado=` (ni `?dia=`, `?filtro=` o `?reserva=`), la pantalla se abre en «Por completar» (diseño, 19 oct) si la
 * persona lo tiene. El Socio no ve reservas: solo las cifras del día (`queryIndicator`, G465). El Profesional, sin
 * permiso.
 */
export function Reservas(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil, membresia, nombreNegocio, ops, negocio } = props;
    const { puede } = usePermisos();
    const contexto = useContextoReservas(ops, negocio, membresia, nombreNegocio);
    const ver = puede(PERMISOS.ver);
    const conPorCompletar = ver && (puede(PERMISOS.completar) || puede(PERMISOS.deshacerNoShow));
    // G843: la insignia de «Por completar» es `counts.pending_completion_total` (todas las jornadas, con el ámbito de quien
    // mira); el panel no las cuenta. Se lee con la jornada de hoy y una sola fila.
    const [porCompletar, setPorCompletar] = useState<number | null>(null);
    const pedido = busqueda.get('apartado');
    useEffect(() => {
        if (!conPorCompletar) {
            return;
        }
        let vivo = true;
        ops.listReservations(negocio, { service_date: hoyEn(contexto.zona), limit: 1 })
            .then((p) => {
                if (vivo) {
                    setPorCompletar(p.counts?.pending_completion_total ?? null);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, contexto.zona, conPorCompletar, pedido]);
    const apartados = [
        { valor: 'dia', etiqueta: 'Reservas del día', corta: 'Del día', icono: 'calendar-days' as const },
        ...(conPorCompletar ? [{ valor: 'por-completar', etiqueta: 'Por completar', corta: 'Por completar', icono: 'list-checks' as const, cifra: porCompletar !== null && porCompletar > 0 ? porCompletar : null }] : []),
    ];

    if (!ver) {
        if (puede(PERMISOS.cifras)) {
            return (
                <div className="rv-rs-pantalla">
                    <CifrasSocio {...props} contexto={contexto} />
                </div>
            );
        }
        return <EstadoSinPermiso seccion="Reservas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    const alDia = busqueda.has('dia') || busqueda.has('filtro') || busqueda.has('reserva') || busqueda.has('nueva');
    const porDefecto = apartados.find((a) => a.valor === 'por-completar') ?? apartados[0];
    const activo = apartados.find((a) => a.valor === pedido) ?? (alDia ? apartados[0] : porDefecto) ?? apartados[0];
    const base = ruta.split('?')[0] ?? ruta;

    return (
        <div className="rv-rs-pantalla">
            {apartados.length > 1 && (
                <div className="rv-en-barra">
                    <Segmentos
                        etiqueta="Apartados de reservas"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono, cifra: 'cifra' in a ? a.cifra : null }))}
                        valor={activo === undefined ? 0 : apartados.indexOf(activo)}
                        onCambio={(i) => {
                            const a = apartados[i];
                            if (a !== undefined) {
                                navegar(rutaConApartado(base, a.valor));
                            }
                        }}
                    />
                </div>
            )}
            {busqueda.get('nueva') === '1' && puede(PERMISOS.crear) && !props.soloLectura && (
                <NuevaReserva
                    abierto
                    movil={movil}
                    ops={ops}
                    negocio={negocio}
                    contexto={contexto}
                    diaInicial={busqueda.get('dia') ?? hoyEn(contexto.zona)}
                    onCerrar={() => { navegar(`${base}?apartado=dia${busqueda.has('dia') ? `&dia=${busqueda.get('dia') ?? ''}` : ''}`); }}
                    onHecha={(id, dia) => { navegar(`${base}?apartado=dia&dia=${dia}&reserva=${encodeURIComponent(id)}`); }}
                />
            )}
            {busqueda.get('reserva') !== null && busqueda.get('nueva') !== '1' && (
                <FichaReserva
                    key={busqueda.get('reserva') ?? ''}
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    soloLectura={props.soloLectura}
                    reservaId={busqueda.get('reserva') ?? ''}
                    contexto={contexto}
                    navegar={navegar}
                    onCerrar={() => {
                        const q = new URLSearchParams(busqueda);
                        q.delete('reserva');
                        navegar(`${base}?${q.toString()}`);
                    }}
                />
            )}
            {activo?.valor === 'por-completar' ? (
                <PorCompletar key="por-completar" {...props} contexto={contexto} />
            ) : (
                <ReservasDelDia key="dia" {...props} contexto={contexto} puedeCompletar={apartados.some((a) => a.valor === 'por-completar')} onPorCompletar={() => { navegar(rutaConApartado(base, 'por-completar')); }} />
            )}
        </div>
    );
}
